import React, { useEffect, useRef, useState } from 'react';
import { Square, Pause, Play, Trash2, Camera, CameraOff, PictureInPicture2, Mic, MicOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FloatingControlsProps {
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: string;
  /** Finish: stops and opens the save screen. */
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  /** Discard the recording (asked to confirm first). */
  onDelete: () => void;
  /** Camera bubble in this recording: show a button to hide/show it. */
  hasCamera?: boolean;
  isCameraOn?: boolean;
  onToggleCamera?: () => void;
  /** Reopen the floating camera preview window (shown while it's closed). */
  onOpenCameraPreview?: () => void;
  /** Browsers without the floating window: small live preview in the controls. */
  inlinePreviewStream?: MediaStream | null;
  /** Microphone in this recording: show a button to mute / unmute it. */
  hasMic?: boolean;
  isMicOn?: boolean;
  onToggleMic?: () => void;
}

const InlineCameraPreview: React.FC<{ stream: MediaStream }> = ({ stream }) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);
  return (
    <video
      ref={ref}
      autoPlay
      muted
      playsInline
      className="w-9 h-9 rounded-full object-cover"
      style={{ backgroundColor: '#000', border: '2px solid rgba(255,255,255,0.7)' }}
    />
  );
};

/** One round control with its name as tooltip and for screen readers. */
const ControlButton: React.FC<{
  label: string;
  onClick: () => void;
  pressed?: boolean;
  tone?: 'plain' | 'danger' | 'off';
  children: React.ReactNode;
}> = ({ label, onClick, pressed, tone = 'plain', children }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    aria-pressed={pressed}
    className="airec-rec-btn"
    data-tone={tone}
  >
    {children}
  </button>
);

const Divider = () => <span aria-hidden className="airec-rec-divider" />;

/**
 * Controls while recording: a bar at the bottom left — status (recording dot or Paused,
 * time), Pause / Resume, Finish, microphone and camera, Discard (confirmed in the bar).
 */
const FloatingControls: React.FC<FloatingControlsProps> = ({
  isRecording,
  isPaused,
  recordingTime,
  onStop,
  onPause,
  onResume,
  onDelete,
  hasCamera = false,
  isCameraOn = true,
  onToggleCamera,
  onOpenCameraPreview,
  inlinePreviewStream,
  hasMic = false,
  isMicOn = true,
  onToggleMic,
}) => {
  const { t } = useTranslation();
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const paused = !isRecording || isPaused;

  return (
    <div
      role="toolbar"
      aria-label={t('rec_controls.label', 'Recording controls')}
      className="airec-rec-bar"
      onKeyDown={(e) => { if (e.key === 'Escape' && confirmDiscard) setConfirmDiscard(false); }}
    >
      {confirmDiscard ? (
        <>
          <span className="text-sm font-medium" style={{ padding: '0 8px' }}>
            {t('rec_controls.discard_question', 'Discard this recording?')}
          </span>
          <button type="button" className="airec-rec-text-btn" data-tone="danger" onClick={onDelete}>
            {t('rec_controls.discard', 'Discard')}
          </button>
          <button type="button" className="airec-rec-text-btn" onClick={() => setConfirmDiscard(false)} autoFocus>
            {t('rec_controls.keep', 'Keep recording')}
          </button>
        </>
      ) : (
        <>
          <span className="flex items-center gap-2" style={{ padding: '0 8px 0 6px' }} role="status">
            <span className={paused ? 'airec-rec-dot is-paused' : 'airec-rec-dot'} aria-hidden />
            <span className="font-mono text-sm font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{recordingTime}</span>
            {paused && <span className="text-xs font-semibold" style={{ color: '#fbbf24' }}>{t('rec_controls.paused', 'Paused')}</span>}
          </span>
          <Divider />
          {paused ? (
            <ControlButton label={t('rec_controls.resume', 'Resume')} onClick={onResume}>
              <Play className="w-4 h-4" fill="currentColor" aria-hidden />
            </ControlButton>
          ) : (
            <ControlButton label={t('rec_controls.pause', 'Pause')} onClick={onPause}>
              <Pause className="w-4 h-4" aria-hidden />
            </ControlButton>
          )}
          <button type="button" className="airec-rec-text-btn" data-tone="danger" onClick={onStop} title={t('rec_controls.finish_hint', 'Stop and review the video')}>
            <Square className="w-3.5 h-3.5" fill="currentColor" aria-hidden />
            {t('rec_controls.finish', 'Finish')}
          </button>
          {(hasMic && onToggleMic) || (hasCamera && onToggleCamera) || inlinePreviewStream ? <Divider /> : null}
          {hasMic && onToggleMic && (
            <ControlButton
              label={isMicOn ? t('mic_mute', 'Mute microphone') : t('mic_unmute', 'Unmute microphone')}
              onClick={onToggleMic}
              pressed={!isMicOn}
              tone={isMicOn ? 'plain' : 'off'}
            >
              {isMicOn ? <Mic className="w-4 h-4" aria-hidden /> : <MicOff className="w-4 h-4" aria-hidden />}
            </ControlButton>
          )}
          {hasCamera && onToggleCamera && (
            <ControlButton
              label={isCameraOn ? t('camera_hide', 'Hide camera') : t('camera_show', 'Show camera')}
              onClick={onToggleCamera}
              pressed={!isCameraOn}
              tone={isCameraOn ? 'plain' : 'off'}
            >
              {isCameraOn ? <Camera className="w-4 h-4" aria-hidden /> : <CameraOff className="w-4 h-4" aria-hidden />}
            </ControlButton>
          )}
          {hasCamera && isCameraOn && onOpenCameraPreview && (
            <ControlButton label={t('camera_preview_open', 'Show camera preview')} onClick={onOpenCameraPreview}>
              <PictureInPicture2 className="w-4 h-4" aria-hidden />
            </ControlButton>
          )}
          {inlinePreviewStream && <InlineCameraPreview stream={inlinePreviewStream} />}
          <Divider />
          <ControlButton label={t('rec_controls.discard', 'Discard')} onClick={() => setConfirmDiscard(true)}>
            <Trash2 className="w-4 h-4" aria-hidden />
          </ControlButton>
        </>
      )}
    </div>
  );
};

export default FloatingControls;
